import { Observable, Subscription } from 'rxjs';

import { Component, OnInit, ViewChild, Inject, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormArray, FormControl, FormBuilder, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { formatDate } from '@angular/common';

import { AppConst } from '../../app-const';
import { ApiReservation, ApiReservationDownloadFile } from '../../api-client-module';
import { ApiReservationQuestion } from '../../api-client-module';
import { ReservationFacility } from '../../firestore-models/reservation-facility';
import { ReservationQuestion } from '../../firestore-models/reservation-question';
import { ReservationFacilityAttribute } from '../../firestore-models/reservation-facility-attribute';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';

import { SelectReservationReservationFacilitiesDialogComponent } from '../select-reservation-reservation-facilities-dialog/select-reservation-reservation-facilities-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';


export interface ReservationDialogData {
  reservation?: ApiReservation;
  reservationFacility?: ReservationFacility | null;
}

@Component({
  selector: 'app-select-reservation-dialog',
  templateUrl: './select-reservation-dialog.component.html',
  styleUrls: ['./select-reservation-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class SelectReservationDialogComponent implements OnInit, OnDestroy {

  editForm : FormGroup;
  statuses: number[] = AppConst.RESERVATION_STATUSES;

  //各予約設備情報に紐付くプロジェクトID
  projectId: string;
  //各予約設備情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  private reservationFacilityAttributesCollection!: AngularFirestoreCollection<ReservationFacilityAttribute>;
  reservationFacilityAttributesObservable!: Observable<ReservationFacilityAttribute[]>;
  reservationFacilityAttributesSubscribe!: Subscription;
  reservationFacilityAttributes!: Array<ReservationFacilityAttribute>;

  projectBaseLang: string = 'ja';
  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理

  constructor(
    public dialogRef: MatDialogRef<SelectReservationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private configurationService: ConfigurationService,
    private angularFirestore: AngularFirestore,
    private translate: TranslateService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
    let startDate = null;
    let endDate = null;
    if (!!data.start_date) {
      const year = parseInt(data.start_date.substring(0, 4));
      const month = parseInt(data.start_date.substring(4, 6));
      const day = parseInt(data.start_date.substring(6, 8));
      startDate = new Date(year, month - 1, day, 0, 0);
    }
    if (!!data.end_date) {
      const year = parseInt(data.end_date.substring(0, 4));
      const month = parseInt(data.end_date.substring(4, 6));
      const day = parseInt(data.end_date.substring(6, 8));
      endDate = new Date(year, month - 1, day, 0, 0);
    }
    this.editForm = this.formBuilder.group({
      facility_attribute_id : new FormControl({value: data.facility_attribute_id, disabled: false }, []),
      facility_id: new FormControl({value: data.facility_id, disabled: false }, []),
      start_date: new FormControl({value: startDate, disabled: false }, []),
      end_date: new FormControl({value: endDate, disabled: false }, []),
      status: new FormControl({value: data.status,  disabled: false }, []),
      reservation_no: new FormControl({value: data.reservation_no, disabled: false }, []),
    });
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });

    // 予約設備属性取得
    this.reservationFacilityAttributesCollection = this.angularFirestore.collection<ReservationFacilityAttribute>(
      AppConst.FIRESTORE_RESERVATION_FACILITY_ATTR, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.reservationFacilityAttributesObservable = this.reservationFacilityAttributesCollection.valueChanges({ idField: 'id' });
    this.reservationFacilityAttributesSubscribe = this.reservationFacilityAttributesObservable.subscribe(reservationFacilityAttributes => {
      this.reservationFacilityAttributes = reservationFacilityAttributes;
    });
  }

  ngOnDestroy(): void {
    this.reservationFacilityAttributesSubscribe.unsubscribe();
  }
  
  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  search(): void {
    const startDate       = !!this.start_date.value ? formatDate(this.start_date.value, 'yyyyMMdd', 'en-US') : null;
    const endDate         = !!this.start_date.value ? formatDate(this.end_date.value, 'yyyyMMdd', 'en-US') : null;
    const data = {
      facility_attribute_id: this.facility_attribute_id.value,
      facility_id: this.facility_id.value,
      start_date: startDate,
      end_date: endDate,
      status: this.status.value,
      reservation_no: this.reservation_no.value,
    }
    this.dialogRef.close(data);
  }

  clearDate(): void {
    this.start_date.setValue(null);
    this.end_date.setValue(null);
  }

  //予約施設選択ダイアログ
  openSelectReservationFacilitiesDialog(): void {
    const dialogRef = this.dialog.open(SelectReservationReservationFacilitiesDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { exhibit: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.facility_id.setValue(result.reservation_facility_id);
        this.reservation_no.setValue('');
      }
    });
  }

  //================================================================================================
  //  getter
  //================================================================================================
  get facility_attribute_id(): AbstractControl { return this.editForm.get('facility_attribute_id')!; }
  get facility_id(): AbstractControl { return this.editForm.get('facility_id')!; }
  get start_date(): AbstractControl { return this.editForm.get('start_date')!; }
  get end_date(): AbstractControl { return this.editForm.get('end_date')!; }
  get status(): AbstractControl { return this.editForm.get('status')!; }
  get reservation_no(): AbstractControl { return this.editForm.get('reservation_no')!; }
}
